import { AudioPlayer } from '@components/AudioPlayer';
import { FileIconLinkButton } from '@components/core-ui/form-elements/buttons/Button/variations/FileIconLinkButton';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import FileIcon from '@components/icons/File.svg';
import { CircularProgress } from '@components/core-ui/spinners/CircularProgress';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { convertDuration } from '@helpers/convertDuration';
import { buildJobMaterialFileName } from '@helpers/buildJobMaterialFileName';
import { formatFileSize } from '@helpers/formatFileSize';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AudioQualityScoreDto, MediaService, PrismaMediaDto } from 'app/API';
import { adminJobsKeys } from 'app/queries/adminJobs/adminJobsKeys';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { createFetchSignedMediaUrlQueryFn } from '../../../../../../../api/fetchSignedMediaUrl';
import { fetchSignedMediaDownloadUrl } from '../../../../../../../api/fetchSignedMediaDownloadUrl';
import { downloadFromUrl } from '@helpers/downloadFromUrl';

interface IAudioWithQualityScoreProps {
  media: PrismaMediaDto;
  signedUrl?: string;
  jobName: string;
}

export const AudioWithQualityScore = ({ media, signedUrl, jobName }: IAudioWithQualityScoreProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [signedUrlState, setSignedUrlState] = useState<string | undefined>(signedUrl);

  const downloadMutation = useMutation({
    mutationFn: async () => {
      const signed = await fetchSignedMediaDownloadUrl(call, { jobId: media.jobId, file: media.fileName });
      if (!signed?.signedUrl) {
        throw new Error('No signed URL');
      }
      return signed.signedUrl;
    },
    onSuccess: async (downloadUrl) => {
      await downloadFromUrl(downloadUrl, buildJobMaterialFileName(jobName, media.fileName));
    },
  });

  const generateUploadParamsMutation = useMutation({
    mutationFn: () =>
      call(() => MediaService.generateUploadParams({ jobId: media.jobId, file: media.fileName }), {
        onError: () => {},
      }),
  });
  const audioQualityScore = media.audioQualityScore ?? media.AudioQualityScore ?? null;
  const qualitySummary = getQualitySummary(audioQualityScore);
  const metrics = useMemo(() => buildQualityMetrics(media, audioQualityScore), [media, audioQualityScore]);

  useEffect(() => {
    setSignedUrlState(signedUrl);
  }, [signedUrl]);

  const handleReplaceClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleDownload = () => downloadMutation.mutateAsync().then(() => undefined);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploading) {
      return;
    }

    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    setIsUploading(true);
    const toastId = toast.loading('Uploading file...');
    try {
      const response = await generateUploadParamsMutation.mutateAsync();

      if (!response?.url || !response?.fields) {
        throw new Error('Failed to get upload parameters');
      }

      const formData = new FormData();
      Object.entries(response.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', file);

      const uploadResp = await fetch(response.url, {
        method: 'PUT',
        body: formData,
      });

      if (!uploadResp.ok) {
        throw new Error('File upload error on S3');
      }

      try {
        const refreshed = await queryClient.fetchQuery({
          queryKey: adminJobsKeys.signedMediaUrl(media.jobId, media.fileName),
          queryFn: createFetchSignedMediaUrlQueryFn(call),
          staleTime: 0,
        });
        if (!refreshed?.signedUrl) {
          toast.remove(toastId);
          toast.error('Failed to get signed URL for the uploaded file');
          return;
        }

        setSignedUrlState(refreshed.signedUrl);
        toast.remove(toastId);
        toast.success('File was replaced successfully');
      } catch {
        toast.remove(toastId);
        toast.error('Failed to get signed URL for the uploaded file');
      }
    } catch (err: unknown) {
      toast.remove(toastId);
      toast.error(err instanceof Error ? err.message : 'File upload error');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="mb-4 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex justify-between items-center mb-2 gap-2">
        <FileIconLinkButton>
          <FileIcon />
          {media.fileName}
        </FileIconLinkButton>
        <OutlinedButton
          type="button"
          style={{ width: 'auto', textTransform: 'uppercase', fontSize: '8px', lineHeight: '12px' }}
          disabled={isUploading}
          onClick={handleReplaceClick}
        >
          {isUploading ? 'Uploading...' : 'replace'}
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            disabled={isUploading}
          />
        </OutlinedButton>
      </div>
      <div className="flex">
        {signedUrlState ? (
          <AudioPlayer
            key={media.id}
            src={signedUrlState}
            downloadFileName={buildJobMaterialFileName(jobName, media.fileName)}
            onDownload={handleDownload}
          />
        ) : (
          <LinearProgressLoader active />
        )}
      </div>

      <details
        className="w-full mb-2 rounded-md"
        style={{ color: qualitySummary.color, backgroundColor: qualitySummary.backgroundColor }}
      >
        <summary className="cursor-pointer px-2 py-2">
          <div className="inline-flex w-[calc(100%-24px)] items-center align-middle">
            <CircularProgress
              indicatorWidth={4}
              indicatorColor={qualitySummary.color}
              progress={qualitySummary.progress}
              size={32}
              className="mr-2"
              trackWidth={4}
              trackColor="var(--color-tint)"
              rotate={270}
            />
            <div className="min-w-[72px] text-sm font-semibold">{qualitySummary.scoreLabel}</div>
            <div className="text-sm ml-2 flex-1">{qualitySummary.text}</div>
          </div>
        </summary>
        {metrics.length ? (
          <div className="grid grid-cols-2 gap-2 px-2 pb-2 text-xs text-[var(--color-text)] md:grid-cols-4">
            {metrics.map((metric) => (
              <div
                key={metric.label}
                className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1"
              >
                <div className="font-medium text-[var(--color-text-muted)]">{metric.label}</div>
                <div className="mt-0.5 font-semibold text-[var(--color-text)]">{metric.value}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-2 pb-2 text-xs text-[var(--color-text-muted)]">
            Pipeline quality details are not available for this file yet.
          </div>
        )}
        {audioQualityScore?.modelVersion || audioQualityScore?.featureVersion ? (
          <div className="px-2 pb-2 text-xs text-[var(--color-text-muted)]">
            {audioQualityScore.modelVersion ? <span>Model: {audioQualityScore.modelVersion}</span> : null}
            {audioQualityScore.modelVersion && audioQualityScore.featureVersion ? <span> · </span> : null}
            {audioQualityScore.featureVersion ? <span>Features: {audioQualityScore.featureVersion}</span> : null}
          </div>
        ) : null}
      </details>
    </div>
  );
};

type TQualityMetric = {
  label: string;
  value: string;
};

const PIPELINE_SCORE_MAX = 5;

const getQualitySummary = (audioQualityScore: AudioQualityScoreDto | null) => {
  const score = getFiniteNumber(audioQualityScore?.proxyScore);
  const band = normalizeScoreBand(audioQualityScore?.scoreBand, score);

  if (score === null) {
    return {
      progress: 0,
      scoreLabel: '---',
      color: 'var(--color-text)',
      backgroundColor: 'var(--color-surface-muted)',
      text: 'Audio quality score is not available yet.',
    };
  }

  if (band === 'poor') {
    return {
      progress: toProgress(score),
      scoreLabel: `${formatNumber(score)} / ${PIPELINE_SCORE_MAX}`,
      color: 'var(--color-quality-poor-text)',
      backgroundColor: 'var(--color-quality-poor-bg)',
      text: 'Poor audio quality score. Review the audio metrics.',
    };
  }

  if (band === 'fair') {
    return {
      progress: toProgress(score),
      scoreLabel: `${formatNumber(score)} / ${PIPELINE_SCORE_MAX}`,
      color: 'var(--color-quality-fair-text)',
      backgroundColor: 'var(--color-quality-fair-bg)',
      text: 'Fair audio quality score. Review the audio metrics.',
    };
  }

  return {
    progress: toProgress(score),
    scoreLabel: `${formatNumber(score)} / ${PIPELINE_SCORE_MAX}`,
    color: 'var(--color-quality-good-text)',
    backgroundColor: 'var(--color-quality-good-bg)',
    text: 'Good audio quality score.',
  };
};

const buildQualityMetrics = (
  media: PrismaMediaDto,
  audioQualityScore: AudioQualityScoreDto | null,
): TQualityMetric[] => {
  const features = audioQualityScore?.features ?? {};
  const duration = getFiniteNumber(media.duration) ?? getFeatureNumber(features, 'duration_seconds');
  const silenceDuration = getFiniteNumber(media.silenceDuration);
  const silenceRatio =
    duration !== null && duration > 0 && silenceDuration !== null ? (silenceDuration / duration) * 100 : null;
  const sampleRate = getFeatureNumber(features, 'sample_rate');
  const bitRate = getFeatureNumber(features, 'bit_rate');
  const speechActivityRatio = getFeatureNumber(features, 'speech_activity_ratio');
  const snr = getFeatureNumber(features, 'snr_db');
  const vadSnr = getFeatureNumber(features, 'vad_snr_db');
  const noiseFloor = getFeatureNumber(features, 'noise_floor_db');
  const integratedLufs = getFeatureNumber(features, 'integrated_lufs');
  const truePeak = getFeatureNumber(features, 'true_peak_dbtp');
  const clipRatio = getFeatureNumber(features, 'clip_ratio');
  const effectiveBandwidth = getFeatureNumber(features, 'effective_bandwidth_hz');
  const hum = getFeatureNumber(features, 'hum_max_rel_db');
  const codec = getFeatureText(features, 'codec');
  const format = getFeatureText(features, 'format_name');
  const channels = getFiniteNumber(media.channels) ?? getFeatureNumber(features, 'channels');
  const fileSize = getFiniteNumber(media.fileSize);

  return [
    buildMetric('Bitrate', formatBitrate(bitRate)),
    buildMetric('Sample rate', sampleRate === null ? null : `${formatNumber(sampleRate, 0)} Hz`),
    buildMetric('Channels', channels === null ? null : formatNumber(channels, 0)),
    buildMetric('Duration', duration === null ? null : convertDuration(duration)),
    buildMetric('Silence', formatSilence(silenceDuration, silenceRatio)),
    buildMetric('Speech activity', formatRatioPercent(speechActivityRatio)),
    buildMetric('SNR', snr === null ? null : `${formatNumber(snr)} dB`),
    buildMetric('VAD SNR', vadSnr === null ? null : `${formatNumber(vadSnr)} dB`),
    buildMetric('Noise floor', noiseFloor === null ? null : `${formatNumber(noiseFloor)} dB`),
    buildMetric('Loudness', integratedLufs === null ? null : `${formatNumber(integratedLufs)} LUFS`),
    buildMetric('True peak', truePeak === null ? null : `${formatNumber(truePeak)} dBTP`),
    buildMetric('Clipping', formatRatioPercent(clipRatio)),
    buildMetric('Bandwidth', effectiveBandwidth === null ? null : `${formatNumber(effectiveBandwidth, 0)} Hz`),
    buildMetric('Hum', hum === null ? null : `${formatNumber(hum)} dB`),
    buildMetric('Codec', codec),
    buildMetric('Format', format),
    buildMetric('File size', fileSize === null ? null : formatFileSize(fileSize)),
    buildMetric('Extension', media.fileExtension || null),
  ].filter((metric): metric is TQualityMetric => metric !== null);
};

const buildMetric = (label: string, value: string | null): TQualityMetric | null => {
  if (!value) {
    return null;
  }

  return { label, value };
};

const normalizeScoreBand = (scoreBand: string | null | undefined, score: number | null): 'poor' | 'fair' | 'good' => {
  if (scoreBand === 'poor' || scoreBand === 'fair' || scoreBand === 'good') {
    return scoreBand;
  }

  if (score !== null && score < 2.5) {
    return 'poor';
  }

  if (score !== null && score < 4) {
    return 'fair';
  }

  return 'good';
};

const toProgress = (score: number) => Math.min(Math.max((score / PIPELINE_SCORE_MAX) * 100, 0), 100);

const formatSilence = (silenceDuration: number | null, silenceRatio: number | null): string | null => {
  if (silenceDuration === null) {
    return null;
  }

  if (silenceRatio === null) {
    return convertDuration(silenceDuration);
  }

  return `${convertDuration(silenceDuration)} (${formatNumber(silenceRatio)}%)`;
};

const formatRatioPercent = (ratio: number | null): string | null => {
  if (ratio === null) {
    return null;
  }

  return `${formatNumber(ratio * 100)}%`;
};

const formatBitrate = (bitRate: number | null): string | null => {
  if (bitRate === null) {
    return null;
  }

  if (bitRate >= 1000) {
    return `${formatNumber(bitRate / 1000)} kbps`;
  }

  return `${formatNumber(bitRate, 0)} bps`;
};

const getFeatureNumber = (features: Record<string, unknown>, key: string): number | null => {
  return getFiniteNumber(features[key]);
};

const getFeatureText = (features: Record<string, unknown>, key: string): string | null => {
  const value = features[key];

  if (typeof value !== 'string' || !value) {
    return null;
  }

  return value;
};

const getFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const formatNumber = (value: number, decimals = 1) => {
  return value.toLocaleString(undefined, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });
};
